// Thin wrapper around the admin/apiKeys.ts httpActions, mirroring
// profileBuilderApi.ts: same session token, same env-following API base.

import { adminSession } from './api';
import { activeConfig } from './environments';

export type AdminScope = string;

/** Display grouping for scope pickers. Defined server-side in lib/adminScopes.ts. */
export type ScopeGroup = {
  id: string;
  label: string;
  description: string;
  scopes: readonly AdminScope[];
};

export type ApiKeyRow = {
  id: string;
  label: string;
  env: 'dev' | 'live';
  scopes: AdminScope[];
  created_at: number;
  expires_at: number | null;
  last_used_at: number | null;
  revoked_at: number | null;
  rotated_to_id: string | null;
  active: boolean;
};

export type KeyListResponse = {
  keys: ApiKeyRow[];
  /** Scopes THIS admin may grant — a superadmin gets all of them. */
  grantable_scopes: AdminScope[];
  all_scopes: AdminScope[];
  scope_groups: ScopeGroup[];
  environment: 'dev' | 'live';
};

/** Returned by mint + rotate. `secret` is shown once and never again. */
export type MintedKey = {
  keyId: string;
  secret: string;
  label: string;
  scopes: AdminScope[];
  env: 'dev' | 'live';
  expiresAt: number | null;
  createdAt: number;
};

export class KeyApiError extends Error {
  constructor(
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(
  path: string,
  init: { method?: string; body?: unknown } = {},
): Promise<T> {
  const token = adminSession.getToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const res = await fetch(activeConfig().apiBase + path, {
    method: init.method ?? 'GET',
    headers,
    body: init.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : null;
  if (!res.ok) {
    const err = data?.error ?? {};
    throw new KeyApiError(err.code ?? 'UNKNOWN', err.message ?? 'Request failed');
  }
  return data as T;
}

export const keysAPI = {
  list: () => request<KeyListResponse>('/admin/keys'),

  mint: (body: { label: string; scopes: AdminScope[]; ttl_days: number | null }) =>
    request<MintedKey>('/admin/keys', { method: 'POST', body }),

  /** Mints a replacement and kills the original immediately. */
  rotate: (id: string) =>
    request<MintedKey>(`/admin/keys/${id}/rotate`, { method: 'POST' }),

  /** Replace a live key's scopes. The secret does not change. */
  updateScopes: (id: string, scopes: AdminScope[]) =>
    request<{ keyId: string; label: string; scopes: AdminScope[] }>(`/admin/keys/${id}`, {
      method: 'PATCH',
      body: { scopes },
    }),

  revoke: (id: string) =>
    request<{ ok: true; alreadyRevoked: boolean }>(`/admin/keys/${id}/revoke`, {
      method: 'POST',
    }),
};

/**
 * Human-readable notes per scope, for every scope picker. Kept here rather
 * than on the server because they describe what the scope means TO AN
 * OPERATOR choosing it, which is a UI concern; the authoritative scope list
 * and its grouping come from the server, so an unknown scope renders with
 * a blank note instead of vanishing.
 */
export const SCOPE_NOTES: Record<string, string> = {
  'users:read': 'View user records.',
  'users:write': 'Edit, suspend and restore users.',
  'moderation:write': 'Action the moderation queue.',
  'reports:write': 'Action DM reports.',
  'payments:read': 'View billing and entitlements.',
  'support:write': 'Respond to support threads.',
  'flags:write': 'Change feature flags.',
  'admins:write': 'Create and edit admin accounts.',
  'synthetic:create': 'Create test accounts and profiles through the API.',
  'synthetic:purge': 'Delete test accounts. Scoped admins can only delete their own.',
  'apikeys:mint': 'Mint, rotate and revoke their own API keys.',
  'docs:read': 'Read the API reference and OpenAPI spec.',
  'blog:read': 'Read blog posts and pillars, drafts included.',
  'blog:write': 'Write, publish and archive blog posts, pillars and images.',
};
